// apps/web/src/features/russia-requests/components/editor/RussiaEditorEndAddressCard.tsx
//
// Free-text delivery-address card. Kept separate from basics so the
// editor host can grow / shrink cards independently as the russia-rates
// flow matures.

import { useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { CreateRussiaRequestManualInput } from '@magimp/shared';
import { Card, Field, inputStyle } from './EditorPrimitives.js';

export interface RussiaEditorEndAddressCardProps {
  form: UseFormReturn<CreateRussiaRequestManualInput>;
  onChanged: () => void;
}

export function RussiaEditorEndAddressCard({ form, onChanged }: RussiaEditorEndAddressCardProps) {
  const { t } = useTranslation('russiaRequests');
  const {
    formState: { errors },
  } = form;
  // WHY: memoize register so the ref callback identity stays stable —
  //   see RussiaEditorBasicsCard for the userEvent-focus rationale.
  const endAddressRegister = useMemo(() => {
    const reg = form.register('endAddress');
    return {
      ...reg,
      onChange: async (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const out = await reg.onChange(e);
        onChanged();
        return out;
      },
    };
  }, [form, onChanged]);

  const error = errors.endAddress ? t('editor.errors.endAddressRequired') : null;

  return (
    <Card title={t('editor.cards.endAddress')}>
      <Field label={t('editor.fields.endAddress')} htmlFor="russia-editor-endAddress" error={error}>
        <textarea
          id="russia-editor-endAddress"
          data-testid="russia-requests-editor-endAddress"
          rows={3}
          maxLength={2000}
          aria-invalid={errors.endAddress ? true : undefined}
          style={{ ...inputStyle, resize: 'vertical' }}
          {...endAddressRegister}
        />
      </Field>
    </Card>
  );
}
